<template>
  <Loader v-if="isLoading" visible/>
  <Notification
      v-if="notificationMessage"
      :message="notificationMessage"
      :type="notificationType"
      :duration="3000"
      @closed="notificationMessage = null"
  />
  <div class="courses-container  py-section">
    <div class="courses-header">
      <div class="courses-title">

        <i class="courses-icon">🧑🏻‍💻</i>
        <h1>Курсы</h1>
      </div>
      <button v-if="isModerator" class="add-course-btn" @click="openCreateModal">
        <i class="add-icon">+</i>
      </button>
    </div>

    <div class="courses-list">
      <div v-for="course in courses" :key="course.id" class="course-card" @click="openCoursePage(course.id)">
        <div class="course-content">
          <div class="course-left">
            <div
              class="course-photo"
              :style="{
                backgroundImage: course.photo ? `url(${course.photo})` : 'none',
                backgroundColor: !course.photo ? 'rgba(255, 255, 255, 0.05)' : 'transparent'
              }"
            >
              <span v-if="!course.photo" class="no-photo-icon">📚</span>
            </div>
          </div>
          <div class="course-right">
            <div class="course-header">
              <h3>{{ course.title }}</h3>
            </div>
            <p>{{ course.description }}</p>
          </div>
          <div class="course-actions" @click.stop v-if="isModerator">
            <button
              class="edit-btn"
              @click="openEditModal(course)"
            >
              ✏️
            </button>
          </div>
        </div>
      </div>
    </div>

    <ModalComponent
        v-if="isEditModalVisible"
        :isVisible="isEditModalVisible"
        title="Edit Course"
        @close="closeEditModal"
    >
      <div>
        <div class="form-group">
          <label for="title">Title</label>
          <input type="text" class="form-control" v-model="currentCourse.title"/>
        </div>
        <div class="form-group">
          <label for="newDescription">Description</label>
          <textarea
              class="form-control form-control-description"
              v-model="currentCourse.description"
              rows="1"
              @input="adjustHeightDescriptionInput($event)">
          </textarea>
        </div>
        <div class="form-group">
          <label for="status">Status</label>
          <select class="form-control" v-model="currentCourse.published">
            <option :value="true">Published</option>
            <option :value="false">Pending</option>
          </select>
        </div>
        <div class="form-group">
          <label for="photo">Photo</label>
          <input type="file" class="form-control" @change="handlePhotoUpload"/>
          <div v-if="previewPhoto" class="preview-photo" :style="{ backgroundImage: `url(${previewPhoto})` }"></div>
        </div>
      </div>
      <template #footer>
        <button class="btn btn-success" @click="saveChanges">✅ Save changes</button>
        <button class="btn btn-danger" @click="deleteCourse(currentCourse?.id)">🗑 Delete</button>
      </template>
    </ModalComponent>

    <ModalComponent
        v-if="isCreateModalVisible"
        :isVisible="isCreateModalVisible"
        title="Create New Course"
        @close="closeCreateModal"
    >
      <div>
        <div class="form-group">
          <label for="newTitle">Title</label>
          <input type="text" class="form-control" v-model="newCourse.title"/>
        </div>
        <div class="form-group">
          <label for="newDescription">Description</label>
          <textarea
              class="form-control form-control-description"
              v-model="newCourse.description"
              rows="1"
              @input="adjustHeightDescriptionInput($event)">
          </textarea>
        </div>
      </div>
      <template #footer>
        <button class="btn btn-success" @click="createCourse">✅ Create</button>
      </template>
    </ModalComponent>
  </div>
</template>

<script>
import CourseDataService from "../../../../services/courses/CourseDataService.js";
import ModalComponent from "../../modal/ModalComponent.vue";
import Notification from "../../Notification.vue";
import Loader from "../../Loader.vue";
import {mapGetters} from "vuex";
import { s3Service } from "../../../../services/s3Service.js";
import { notificationMixin } from "../../../../mixins/notificationMixin"

export default {
  components: {Notification, ModalComponent, Loader},
  data() {
    return {
      isLoading: false,
      courses: [],
      currentCourse: null,
      newCourse: {title: '', description: '', published: false},
      notificationMessage: null,
      notificationType: "info",
      isEditModalVisible: false,
      isCreateModalVisible: false,
      previewPhoto: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['isAdmin', 'isModerator', 'hasRole']),
  },
  mixins: [notificationMixin],
  methods: {
    showNotification(message, type = "info") {
      this.notificationMessage = message;
      this.notificationType = type;
    },
    adjustHeightDescriptionInput(event) {
      const element = event.target;
      element.style.height = 'auto';
      element.style.height = `${element.scrollHeight}px`;
    },
    async handlePhotoUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      try {
        const photoUrl = await s3Service.uploadFile(
            file,
            'courses/photos'
        );

        this.currentCourse.photo = photoUrl;

        const reader = new FileReader();
        reader.onload = (e) => {
            this.previewPhoto = e.target.result;
        };
        reader.readAsDataURL(file);

      } catch (error) {
        console.error("Error uploading photo:", error);
        this.showNotification("Failed to upload photo", "error");
      }
    },

    async retrieveCourses() {
      this.isLoading = true;
      try {
        const response = await CourseDataService.getAll();
        const courses = response.data;

        for (let course of courses) {
          if (course.photo) {
            try {
              course.photo = await s3Service.getDownloadLink(course.photo);
            } catch (photoError) {
              console.error(`Error loading photo for course ${course.id}:`, photoError);
            }
          }
        }

        this.courses = courses;
      } catch (e) {
        console.error("Error loading courses:", e);
        this.showNotification("Failed to load courses", "error");
      } finally {
        this.isLoading = false;
      }
    },
    openEditModal(course) {
      this.currentCourse = course;
      this.isEditModalVisible = true;
    },
    closeEditModal() {
      this.isEditModalVisible = false;
    },
    async saveChanges() {
      try {
        await CourseDataService.update(this.currentCourse.id, this.currentCourse);
        this.refreshList();
        this.closeEditModal();
        this.previewPhoto = null;
        this.$root.showNotification("The course was updated successfully!", "success");
      } catch (e) {
        console.log(e);
        this.$root.showNotification("The course was updated with error!", "error");
      }
    },
    async deleteCourse(courseId) {
      if (window.confirm("Are you sure you want to delete this courses?")) {
        try {
          await CourseDataService.delete(courseId);
          this.refreshList();
          this.closeEditModal();
          this.previewPhoto = null;
          this.$root.showNotification("The course was deleted successfully!", "success");
        } catch (e) {
          console.log(e);
          this.$root.showNotification("The course was deleted with error!", "error");
        }
      }
    },
    refreshList() {
      this.retrieveCourses();
      this.currentCourse = null;
    },
    openCreateModal() {
      this.isCreateModalVisible = true;
    },
    closeCreateModal() {
      this.isCreateModalVisible = false;
    },
    async createCourse() {
      try {
        await CourseDataService.create(this.newCourse);
        this.refreshList();
        this.closeCreateModal();
        this.newCourse = {title: '', description: '', published: false};
        this.$root.showNotification("The course was created successfully!", "success");
      } catch (e) {
        console.log(e);
        this.$root.showNotification("The course was created with error!", "error");
      }
    },
    openCoursePage(id) {
      this.$router.push(`/courses/${id}`);
    },
  },
  mounted() {
    this.retrieveCourses();
  }
};
</script>

<style scoped>
.courses-container {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 20px;
  backdrop-filter: blur(7px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 30px;
  padding: 10px 10px;
  margin-left: 10px;
  margin-right: 10px;
}

.courses-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.courses-title {
  display: flex;
  align-items: center;
  gap: 7px;
}

.courses-title h2 {
  margin: 0;
  color: var(--my-light);
  font-size: 24px;
  font-weight: 600;
}

.courses-icon {
  font-size: 33px;
  filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
}

.add-course-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--my-links);
  color: var(--my-light);
  border: none;
  padding: 1px 10px;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
  outline: none !important;
  margin-right: 5px;
  margin-top: 5px;
}

.add-course-btn:hover {
  background: var(--my-accent);
  transform: translateY(-2px);
}

.add-icon {
  font-size: 20px;
  font-weight: bold;
}

.courses-list {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-top: var(--space-inner);
}

.course-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.course-card:hover {
  transform: translateY(-5px);
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.course-content {
  display: flex;
  flex-direction: row;
  gap: var(--space-element);
}

.course-left {
  width: 70px;
  height: 70px;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}

.course-right {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.course-photo {
  width: 100%;
  aspect-ratio: 1;
  background-size: cover;
  background-position: center;
  border-radius: 12px;
  background-color: var(--my-light) !important;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.course-left::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 12px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.course-card:hover .course-left::after {
  opacity: 1;
}

.course-header {
  margin: 0 0 4px 0;
  font-size: 15px;
  font-weight: 600;
}

.course-content h3 {
  color: var(--my-light);
  font-size: 15px;
  font-weight: 600;
}

.course-content p {
  color: var(--my-white);
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: 'Montserrat', sans-serif;
  font-weight: 200;
}

.course-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 1;
  position: absolute;
  left: 33px;
  top: 33px;
}

.course-card:hover .course-actions {
  opacity: 1;
}

.edit-btn {
  background: var(--my-links);
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s ease, background-color 0.2s ease;
  outline: none !important;
  font-size: 12px;
}

.edit-btn:hover {
  background-color: var(--my-accent);
  transform: scale(1.1);
}

@media (max-width: 1400px) {
  .courses-list {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 1024px) {
  .courses-list {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .courses-header {
    flex-direction: row;
    gap: 15px;
    align-items: flex-start;
  }

  .courses-list {
    grid-template-columns: 1fr;
  }

  .course-left {
    width: 60px;
    height: 60px;
  }

  .course-actions {
    position: static;
    opacity: 1;
    margin-left: auto;
    margin-right: 10px;
  }

  .edit-btn {
    width: 33px;
    height: 33px;
    background: var(--my-links);
  }

  .add-course-btn {
    margin-right: 0;
  }

  .edit-btn:hover {
    transform: none;
    background: var(--my-accent);
  }
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: var(--my-white);
  font-weight: 400;
}

.form-control {
  width: 100%;
  padding: 10px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  background: var(--my-white);
}

.form-control-description {
  min-height: 100px;
  resize: vertical;
}

.preview-photo {
  width: 100%;
  height: 160px;
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  margin-top: 10px;
  border: 1px solid var(--my-border);
}

.no-photo-icon {
  font-size: 24px;
  opacity: 0.5;
}
</style>
